import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lightbulb,
  Thermometer,
  Shield,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Volume2,
  Home,
  Film,
  LogOut,
  Mic,
  MicOff,
  Droplets,
  Sun,
  Zap,
  Plus,
  Minus,
  Power,
  CheckCircle,
  Eye,
  Camera,
  AlertTriangle } from
'lucide-react';

interface DeviceStates {
  lights: {
    sala: boolean;
    cocina: boolean;
    brightness: number;
  };
  thermostat: {
    on: boolean;
    temperature: number;
  };
  security: {
    alarm: boolean;
    camera: boolean;
  };
  multimedia: {
    playing: boolean;
    volume: number;
    currentSong: string;
  };
  sensors: {
    humidity: number;
    uvIndex: number;
    humidityStatus: string;
    uvStatus: string;
  };
}

interface VoiceCommand {
  text: string;
  action: () => void;
  response: string;
}

const HomeAutomationSimulator: React.FC = () => {
  const [devices, setDevices] = useState<DeviceStates>({
    lights: {
      sala: false,
      cocina: false,
      brightness: 70
    },
    thermostat: {
      on: false,
      temperature: 22
    },
    security: {
      alarm: false,
      camera: false
    },
    multimedia: {
      playing: false,
      volume: 50,
      currentSong: "The Night We Met"
    },
    sensors: {
      humidity: 45,
      uvIndex: 3,
      humidityStatus: "Nivel Óptimo",
      uvStatus: "Bajo"
    }
  });

  const [energyConsumption, setEnergyConsumption] = useState(0);
  const [hourlyCost, setHourlyCost] = useState(0);
  const [voiceResponse, setVoiceResponse] = useState<string>("");
  const [currentTime, setCurrentTime] = useState(new Date());

  // Actualizar hora cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Calcular consumo energético en tiempo real
  useEffect(() => {
    let consumption = 0;

    // Luces
    if (devices.lights.sala) consumption += 60;
    if (devices.lights.cocina) consumption += 45;

    // Termostato
    if (devices.thermostat.on) consumption += 1200;

    // Multimedia
    if (devices.multimedia.playing) consumption += 85;

    // Seguridad
    if (devices.security.alarm) consumption += 15;
    if (devices.security.camera) consumption += 25;

    // Consumo base del sistema
    consumption += 50;

    setEnergyConsumption(consumption);
    setHourlyCost(consumption * 0.0025); // $0.0025 por W/h
  }, [devices]);

  // Escenas predefinidas
  const executeScene = (scene: string) => {
    switch (scene) {
      case 'llegada':
        setDevices((prev) => ({
          ...prev,
          lights: { ...prev.lights, sala: true, cocina: true, brightness: 80 },
          thermostat: { on: true, temperature: 23 },
          security: { alarm: false, camera: true },
          multimedia: { ...prev.multimedia, playing: true, volume: 40 }
        }));
        break;
      case 'pelicula':
        setDevices((prev) => ({
          ...prev,
          lights: { ...prev.lights, sala: false, cocina: false, brightness: 20 },
          thermostat: { on: true, temperature: 21 },
          multimedia: { ...prev.multimedia, playing: true, volume: 75 }
        }));
        break;
      case 'salida':
        setDevices((prev) => ({
          ...prev,
          lights: { sala: false, cocina: false, brightness: 0 },
          thermostat: { on: false, temperature: 25 },
          security: { alarm: true, camera: true },
          multimedia: { ...prev.multimedia, playing: false, volume: 0 }
        }));
        break;
    }
  };

  // Comandos de voz
  const voiceCommands: VoiceCommand[] = [
  {
    text: "Encender luz sala",
    action: () => setDevices((prev) => ({ ...prev, lights: { ...prev.lights, sala: true } })),
    response: "Ok, encendiendo la luz de la sala."
  },
  {
    text: "Apagar luz cocina",
    action: () => setDevices((prev) => ({ ...prev, lights: { ...prev.lights, cocina: false } })),
    response: "Ok, apagando la luz de la cocina."
  },
  {
    text: "Activar escena Película",
    action: () => executeScene('pelicula'),
    response: "Activando escena Noche de Película."
  },
  {
    text: "Apagar todo",
    action: () => setDevices((prev) => ({
      ...prev,
      lights: { sala: false, cocina: false, brightness: 0 },
      thermostat: { on: false, temperature: 25 },
      multimedia: { ...prev.multimedia, playing: false, volume: 0 }
    })),
    response: "Ok, apagando todos los dispositivos."
  },
  {
    text: "Subir temperatura",
    action: () => setDevices((prev) => ({
      ...prev,
      thermostat: {
        on: true,
        temperature: Math.min(30, prev.thermostat.temperature + 2)
      }
    })),
    response: "Ok, subiendo la temperatura."
  },
  {
    text: "Activar seguridad",
    action: () => setDevices((prev) => ({ ...prev, security: { alarm: true, camera: true } })),
    response: "Ok, activando el sistema de seguridad."
  }];


  const executeVoiceCommand = (command: VoiceCommand) => {
    command.action();
    setVoiceResponse(command.response);
    setTimeout(() => setVoiceResponse(""), 3000);
  };

  const getLightIconColor = () => {
    if (devices.lights.sala || devices.lights.cocina) {
      return "text-yellow-400 drop-shadow-[0_0_10px_rgba(251,191,36,0.7)]";
    }
    return "text-gray-400";
  };

  const getThermostatIconColor = () => {
    return devices.thermostat.on ? "text-red-400" : "text-gray-400";
  };

  const getSecurityIconColor = () => {
    return devices.security.alarm || devices.security.camera ? "text-blue-400" : "text-gray-400";
  };

  const getMultimediaIconColor = () => {
    return devices.multimedia.playing ? "text-green-400" : "text-gray-400";
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white font-['Inter']" data-id="zky9hcdks" data-path="src/components/HomeAutomationSimulator.tsx">
      {/* Header */}
      <div className="text-center py-8" data-id="6uky41zb7" data-path="src/components/HomeAutomationSimulator.tsx">
        <h1 className="text-4xl font-bold mb-2" data-id="6ckqw3xwi" data-path="src/components/HomeAutomationSimulator.tsx">Centro de Control del Hogar</h1>
        <p className="text-gray-400 text-lg" data-id="vzyz2g44v" data-path="src/components/HomeAutomationSimulator.tsx">Gestiona tus dispositivos, crea escenas y usa comandos de voz</p>
      </div>

      {/* Banner de Consumo Energético */}
      <div className="max-w-6xl mx-auto px-6 mb-8" data-id="l1n3c4xp9" data-path="src/components/HomeAutomationSimulator.tsx">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 shadow-2xl" data-id="fezyh8ubs" data-path="src/components/HomeAutomationSimulator.tsx">
          <div className="flex justify-around items-center" data-id="qqiksi8ay" data-path="src/components/HomeAutomationSimulator.tsx">
            <div className="text-center" data-id="ghet4wwc8" data-path="src/components/HomeAutomationSimulator.tsx">
              <div className="text-3xl font-bold text-white mb-1" data-id="0wtwclbi4" data-path="src/components/HomeAutomationSimulator.tsx">
                {energyConsumption.toLocaleString()} W
              </div>
              <div className="text-blue-100 text-sm" data-id="3nmmf1otq" data-path="src/components/HomeAutomationSimulator.tsx">Consumo Actual</div>
            </div>
            <div className="text-center" data-id="wfpseg88x" data-path="src/components/HomeAutomationSimulator.tsx">
              <div className="text-3xl font-bold text-white mb-1" data-id="pwnic7ck3" data-path="src/components/HomeAutomationSimulator.tsx">
                ${hourlyCost.toFixed(2)}
              </div>
              <div className="text-blue-100 text-sm" data-id="dzecvpi4k" data-path="src/components/HomeAutomationSimulator.tsx">Costo Estimado / hora</div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid de Paneles de Control */}
      <div className="max-w-6xl mx-auto px-6" data-id="x2i9fpaa5" data-path="src/components/HomeAutomationSimulator.tsx">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="bxfyp9sf1" data-path="src/components/HomeAutomationSimulator.tsx">
          
          {/* Tarjeta Iluminación */}
          <Card className="bg-gray-800 border-gray-700 p-6" data-id="hyvyivl4w" data-path="src/components/HomeAutomationSimulator.tsx">
            <div className="flex items-center gap-3 mb-4" data-id="1dy4fqvr3" data-path="src/components/HomeAutomationSimulator.tsx">
              <Lightbulb className={`w-6 h-6 ${getLightIconColor()}`} data-id="akmymxg4r" data-path="src/components/HomeAutomationSimulator.tsx" />
              <h3 className="text-xl font-semibold" data-id="uxd6k3sky" data-path="src/components/HomeAutomationSimulator.tsx">Iluminación</h3>
            </div>
            
            <div className="space-y-4" data-id="4gbvgy3sz" data-path="src/components/HomeAutomationSimulator.tsx">
              <div className="flex items-center justify-between" data-id="bz6vlir8q" data-path="src/components/HomeAutomationSimulator.tsx">
                <span className="text-white" data-id="xegq7gk3j" data-path="src/components/HomeAutomationSimulator.tsx">Luz Sala</span>
                <div className="flex items-center gap-2" data-id="qqnpr0zim" data-path="src/components/HomeAutomationSimulator.tsx">
                  <span className="text-sm text-gray-400" data-id="p8y57lc72" data-path="src/components/HomeAutomationSimulator.tsx">
                    {devices.lights.sala ? "Encendida" : "Apagada"}
                  </span>
                  <Button
                    variant={devices.lights.sala ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDevices((prev) => ({
                      ...prev,
                      lights: { ...prev.lights, sala: !prev.lights.sala }
                    }))} data-id="0rxl1bjlh" data-path="src/components/HomeAutomationSimulator.tsx">

                    <Power className="w-4 h-4" data-id="1bd2zckh1" data-path="src/components/HomeAutomationSimulator.tsx" />
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center justify-between" data-id="p0vo6tdtg" data-path="src/components/HomeAutomationSimulator.tsx">
                <span className="text-white" data-id="e7u7jjre1" data-path="src/components/HomeAutomationSimulator.tsx">Luz Cocina</span>
                <div className="flex items-center gap-2" data-id="uxx3urylm" data-path="src/components/HomeAutomationSimulator.tsx">
                  <span className="text-sm text-gray-400" data-id="d3w35l4aa" data-path="src/components/HomeAutomationSimulator.tsx">
                    {devices.lights.cocina ? "Encendida" : "Apagada"}
                  </span>
                  <Button
                    variant={devices.lights.cocina ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDevices((prev) => ({
                      ...prev,
                      lights: { ...prev.lights, cocina: !prev.lights.cocina }
                    }))} data-id="yzctj5cgh" data-path="src/components/HomeAutomationSimulator.tsx">

                    <Power className="w-4 h-4" data-id="yxzpik6lr" data-path="src/components/HomeAutomationSimulator.tsx" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Tarjeta Termostato */}
          <Card className="bg-gray-800 border-gray-700 p-6" data-id="d5q1izyne" data-path="src/components/HomeAutomationSimulator.tsx">
            <div className="flex items-center gap-3 mb-4" data-id="0ytdf2r29" data-path="src/components/HomeAutomationSimulator.tsx">
              <Thermometer className={`w-6 h-6 ${getThermostatIconColor()}`} data-id="fshpex387" data-path="src/components/HomeAutomationSimulator.tsx" />
              <h3 className="text-xl font-semibold" data-id="put1c5heq" data-path="src/components/HomeAutomationSimulator.tsx">Termostato</h3>
            </div>
            
            <div className="text-center mb-4" data-id="kcq4ii8hy" data-path="src/components/HomeAutomationSimulator.tsx">
              <div className="text-4xl font-bold mb-2" data-id="nt9jedy4b" data-path="src/components/HomeAutomationSimulator.tsx">{devices.thermostat.temperature}°C</div>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-4" data-id="sckqlcd7r" data-path="src/components/HomeAutomationSimulator.tsx">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDevices((prev) => ({
                  ...prev,
                  thermostat: {
                    ...prev.thermostat,
                    temperature: Math.max(16, prev.thermostat.temperature - 1)
                  }
                }))} data-id="nhn520rjk" data-path="src/components/HomeAutomationSimulator.tsx">

                <Minus className="w-4 h-4" data-id="tn15ghp3g" data-path="src/components/HomeAutomationSimulator.tsx" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDevices((prev) => ({
                  ...prev,
                  thermostat: {
                    ...prev.thermostat,
                    temperature: Math.min(30, prev.thermostat.temperature + 1)
                  }
                }))} data-id="c746p2ki2" data-path="src/components/HomeAutomationSimulator.tsx">

                <Plus className="w-4 h-4" data-id="y94wsy9o0" data-path="src/components/HomeAutomationSimulator.tsx" />
              </Button>
            </div>
            
            <Button
              variant={devices.thermostat.on ? "default" : "outline"}
              className="w-full"
              onClick={() => setDevices((prev) => ({
                ...prev,
                thermostat: { ...prev.thermostat, on: !prev.thermostat.on }
              }))} data-id="qa3zs5k4u" data-path="src/components/HomeAutomationSimulator.tsx">

              <Power className="w-4 h-4 mr-2" data-id="o6k0oysog" data-path="src/components/HomeAutomationSimulator.tsx" />
              {devices.thermostat.on ? "Encendido" : "Apagado"}
            </Button>
          </Card>

          {/* Tarjeta Seguridad */}
          <Card className="bg-gray-800 border-gray-700 p-6" data-id="8fyhqmd9p" data-path="src/components/HomeAutomationSimulator.tsx">
            <div className="flex items-center gap-3 mb-4" data-id="au56nlp66" data-path="src/components/HomeAutomationSimulator.tsx">
              <Shield className={`w-6 h-6 ${getSecurityIconColor()}`} data-id="o9mods1mz" data-path="src/components/HomeAutomationSimulator.tsx" />
              <h3 className="text-xl font-semibold" data-id="o8em2xdtc" data-path="src/components/HomeAutomationSimulator.tsx">Seguridad</h3>
            </div>
            
            <div className="space-y-4" data-id="g6r84dzoc" data-path="src/components/HomeAutomationSimulator.tsx">
              <div className="flex items-center justify-between" data-id="07wsjcvs6" data-path="src/components/HomeAutomationSimulator.tsx">
                <span className="text-white" data-id="mf9wehx20" data-path="src/components/HomeAutomationSimulator.tsx">Alarma</span>
                <div className="flex items-center gap-2" data-id="owl80a5qk" data-path="src/components/HomeAutomationSimulator.tsx">
                  <span className="text-sm text-gray-400" data-id="kcigomfqe" data-path="src/components/HomeAutomationSimulator.tsx">
                    {devices.security.alarm ? "Activa" : "Inactiva"}
                  </span>
                  <Button
                    variant={devices.security.alarm ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDevices((prev) => ({
                      ...prev,
                      security: { ...prev.security, alarm: !prev.security.alarm }
                    }))} data-id="rm352cihu" data-path="src/components/HomeAutomationSimulator.tsx">

                    <Power className="w-4 h-4" data-id="ks57qn9dy" data-path="src/components/HomeAutomationSimulator.tsx" />
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center justify-between" data-id="k2z7d7tkq" data-path="src/components/HomeAutomationSimulator.tsx">
                <span className="text-white" data-id="w369jmri3" data-path="src/components/HomeAutomationSimulator.tsx">Cámara</span>
                <div className="flex items-center gap-2" data-id="woic1ejoa" data-path="src/components/HomeAutomationSimulator.tsx">
                  <span className="text-sm text-gray-400" data-id="dxwlqiwuw" data-path="src/components/HomeAutomationSimulator.tsx">
                    {devices.security.camera ? "Activa" : "Inactiva"}
                  </span>
                  <Button
                    variant={devices.security.camera ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDevices((prev) => ({
                      ...prev,
                      security: { ...prev.security, camera: !prev.security.camera }
                    }))} data-id="tfk0niypv" data-path="src/components/HomeAutomationSimulator.tsx">

                    <Power className="w-4 h-4" data-id="7oc6fobag" data-path="src/components/HomeAutomationSimulator.tsx" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Tarjeta Multimedia */}
          <Card className="bg-gray-800 border-gray-700 p-6" data-id="wscgcujdz" data-path="src/components/HomeAutomationSimulator.tsx">
            <div className="flex items-center gap-3 mb-4" data-id="qxrqm6qyc" data-path="src/components/HomeAutomationSimulator.tsx">
              <Play className={`w-6 h-6 ${getMultimediaIconColor()}`} data-id="bfokrvrth" data-path="src/components/HomeAutomationSimulator.tsx" />
              <h3 className="text-xl font-semibold" data-id="jurkve3ak" data-path="src/components/HomeAutomationSimulator.tsx">Multimedia</h3>
            </div>
            
            <div className="text-center mb-4" data-id="5jsfmui72" data-path="src/components/HomeAutomationSimulator.tsx">
              <div className="text-sm text-gray-400 mb-1" data-id="uwxeoql5q" data-path="src/components/HomeAutomationSimulator.tsx">
                {devices.multimedia.playing ? "Reproduciendo" : "En Pausa"}
              </div>
              <div className="font-medium" data-id="6v7ubpaq5" data-path="src/components/HomeAutomationSimulator.tsx">{devices.multimedia.currentSong}</div>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-4" data-id="sj5lukklf" data-path="src/components/HomeAutomationSimulator.tsx">
              <Button variant="outline" size="sm" data-id="ci4ti0wee" data-path="src/components/HomeAutomationSimulator.tsx">
                <SkipBack className="w-4 h-4" data-id="u022emle9" data-path="src/components/HomeAutomationSimulator.tsx" />
              </Button>
              <Button
                variant={devices.multimedia.playing ? "default" : "outline"}
                size="sm"
                onClick={() => setDevices((prev) => ({
                  ...prev,
                  multimedia: { ...prev.multimedia, playing: !prev.multimedia.playing }
                }))} data-id="o3l0sw1zn" data-path="src/components/HomeAutomationSimulator.tsx">

                {devices.multimedia.playing ? <Pause className="w-4 h-4" data-id="kl2nsnfvd" data-path="src/components/HomeAutomationSimulator.tsx" /> : <Play className="w-4 h-4" data-id="5vsx0d6ut" data-path="src/components/HomeAutomationSimulator.tsx" />}
              </Button>
              <Button variant="outline" size="sm" data-id="4zbb105ac" data-path="src/components/HomeAutomationSimulator.tsx">
                <SkipForward className="w-4 h-4" data-id="w02z7pm9z" data-path="src/components/HomeAutomationSimulator.tsx" />
              </Button>
            </div>
            
            <div className="space-y-2" data-id="003t3slzo" data-path="src/components/HomeAutomationSimulator.tsx">
              <div className="flex items-center justify-between" data-id="xw1kl5kd1" data-path="src/components/HomeAutomationSimulator.tsx">
                <Volume2 className="w-4 h-4" data-id="aya22rxid" data-path="src/components/HomeAutomationSimulator.tsx" />
                <span className="text-sm text-white" data-id="rhoeqm1n7" data-path="src/components/HomeAutomationSimulator.tsx">{devices.multimedia.volume}%</span>
              </div>
              <Slider
                value={[devices.multimedia.volume]}
                onValueChange={(value) => setDevices((prev) => ({
                  ...prev,
                  multimedia: { ...prev.multimedia, volume: value[0] }
                }))}
                max={100}
                step={1}
                className="w-full" data-id="6gg9ibrge" data-path="src/components/HomeAutomationSimulator.tsx" />

            </div>
          </Card>

          {/* Tarjeta Sensores Ambientales */}
          <Card className="bg-gray-800 border-gray-700 p-6" data-id="43z2osfjv" data-path="src/components/HomeAutomationSimulator.tsx">
            <div className="flex items-center gap-3 mb-4" data-id="54toz9j5u" data-path="src/components/HomeAutomationSimulator.tsx">
              <Eye className="w-6 h-6 text-purple-400" data-id="ciwp6il9v" data-path="src/components/HomeAutomationSimulator.tsx" />
              <h3 className="text-xl font-semibold" data-id="yhwpftyyz" data-path="src/components/HomeAutomationSimulator.tsx">Sensores Ambientales</h3>
            </div>
            
            <div className="space-y-4" data-id="spk8nupxi" data-path="src/components/HomeAutomationSimulator.tsx">
              <div className="flex items-center justify-between" data-id="hvakdii8k" data-path="src/components/HomeAutomationSimulator.tsx">
                <div className="flex items-center gap-2" data-id="zyqu0y0hu" data-path="src/components/HomeAutomationSimulator.tsx">
                  <Droplets className="w-5 h-5 text-blue-400" data-id="hihx0hj7k" data-path="src/components/HomeAutomationSimulator.tsx" />
                  <span className="text-white" data-id="worzt9pmy" data-path="src/components/HomeAutomationSimulator.tsx">Humedad</span>
                </div>
                <div className="text-right" data-id="7ccciu6un" data-path="src/components/HomeAutomationSimulator.tsx">
                  <div className="font-medium" data-id="cpgj6oz18" data-path="src/components/HomeAutomationSimulator.tsx">{devices.sensors.humidity}%</div>
                  <div className="text-sm text-gray-400" data-id="ctbt6ennp" data-path="src/components/HomeAutomationSimulator.tsx">{devices.sensors.humidityStatus}</div>
                </div>
              </div>
              
              <div className="flex items-center justify-between" data-id="quzk807m2" data-path="src/components/HomeAutomationSimulator.tsx">
                <div className="flex items-center gap-2" data-id="02hp7xvce" data-path="src/components/HomeAutomationSimulator.tsx">
                  <Sun className="w-5 h-5 text-yellow-400" data-id="l4zezez54" data-path="src/components/HomeAutomationSimulator.tsx" />
                  <span className="text-white" data-id="lu64s0a7n" data-path="src/components/HomeAutomationSimulator.tsx">Índice UV</span>
                </div>
                <div className="text-right" data-id="860hesda9" data-path="src/components/HomeAutomationSimulator.tsx">
                  <div className="font-medium" data-id="a6ha2r26x" data-path="src/components/HomeAutomationSimulator.tsx">{devices.sensors.uvIndex}</div>
                  <div className="text-sm text-gray-400" data-id="ps54n9hxy" data-path="src/components/HomeAutomationSimulator.tsx">{devices.sensors.uvStatus}</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Tarjeta Escenas Predefinidas */}
          <Card className="bg-gray-800 border-gray-700 p-6" data-id="w5x9e3r1o" data-path="src/components/HomeAutomationSimulator.tsx">
            <div className="flex items-center gap-3 mb-4" data-id="z2kegnuw9" data-path="src/components/HomeAutomationSimulator.tsx">
              <Film className="w-6 h-6 text-indigo-400" data-id="ffgg0j5w3" data-path="src/components/HomeAutomationSimulator.tsx" />
              <h3 className="text-xl font-semibold" data-id="rfps1hf78" data-path="src/components/HomeAutomationSimulator.tsx">Escenas Predefinidas</h3>
            </div>
            
            <div className="space-y-3" data-id="ccujuca45" data-path="src/components/HomeAutomationSimulator.tsx">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => executeScene('llegada')} data-id="epd4zggmz" data-path="src/components/HomeAutomationSimulator.tsx">

                <Home className="w-4 h-4 mr-2" data-id="xhri29lw5" data-path="src/components/HomeAutomationSimulator.tsx" />
                Llegar a Casa
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => executeScene('pelicula')} data-id="q8zmd4wnz" data-path="src/components/HomeAutomationSimulator.tsx">

                <Film className="w-4 h-4 mr-2" data-id="werdtrubo" data-path="src/components/HomeAutomationSimulator.tsx" />
                Noche de Película
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => executeScene('salida')} data-id="napik4vir" data-path="src/components/HomeAutomationSimulator.tsx">

                <LogOut className="w-4 h-4 mr-2" data-id="zulcuencb" data-path="src/components/HomeAutomationSimulator.tsx" />
                Salir de Casa
              </Button>
            </div>
          </Card>
        </div>

        {/* Tarjeta Asistente de Voz */}
        <Card className="bg-gray-800 border-gray-700 p-6 mt-6" data-id="cjaj2hb3a" data-path="src/components/HomeAutomationSimulator.tsx">
          <div className="flex items-center gap-3 mb-4" data-id="e1co1gcs3" data-path="src/components/HomeAutomationSimulator.tsx">
            <Mic className="w-6 h-6 text-green-400" data-id="z27rywxmd" data-path="src/components/HomeAutomationSimulator.tsx" />
            <h3 className="text-xl font-semibold" data-id="zna6w4sdd" data-path="src/components/HomeAutomationSimulator.tsx">Asistente de Voz (Simulado)</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4" data-id="8137f131d" data-path="src/components/HomeAutomationSimulator.tsx">
            {voiceCommands.map((command, index) =>
            <Button
              key={index}
              variant="outline"
              className="text-left justify-start"
              onClick={() => executeVoiceCommand(command)} data-id="swgqhg9mz" data-path="src/components/HomeAutomationSimulator.tsx">

                <Mic className="w-4 h-4 mr-2" data-id="nu4wmuvke" data-path="src/components/HomeAutomationSimulator.tsx" />
                {command.text}
              </Button>
            )}
          </div>
          
          <AnimatePresence data-id="e8tcsq1s7" data-path="src/components/HomeAutomationSimulator.tsx">
            {voiceResponse &&
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-green-900/30 border border-green-700 rounded-lg p-4 flex items-center gap-3" data-id="duzfwz6xx" data-path="src/components/HomeAutomationSimulator.tsx">

                <CheckCircle className="w-5 h-5 text-green-400" data-id="bkwm2irl3" data-path="src/components/HomeAutomationSimulator.tsx" />
                <span className="text-green-100" data-id="3ltge35hh" data-path="src/components/HomeAutomationSimulator.tsx">{voiceResponse}</span>
              </motion.div>
            }
          </AnimatePresence>
        </Card>
      </div>

      {/* Footer con información adicional */}
      <div className="max-w-6xl mx-auto px-6 py-8 mt-12" data-id="3qeg4dxzd" data-path="src/components/HomeAutomationSimulator.tsx">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="ly0hicbw1" data-path="src/components/HomeAutomationSimulator.tsx">
          <div className="text-center" data-id="bzlf1furj" data-path="src/components/HomeAutomationSimulator.tsx">
            <Zap className="w-12 h-12 mx-auto mb-3 text-yellow-400" data-id="jongiasg4" data-path="src/components/HomeAutomationSimulator.tsx" />
            <h4 className="text-lg font-semibold mb-2" data-id="uhl6zc7m1" data-path="src/components/HomeAutomationSimulator.tsx">Ahorro Energético</h4>
            <p className="text-gray-400" data-id="7ljnq90gj" data-path="src/components/HomeAutomationSimulator.tsx">Reduce tu consumo hasta un 30%</p>
          </div>
          <div className="text-center" data-id="jeyayho6m" data-path="src/components/HomeAutomationSimulator.tsx">
            <Shield className="w-12 h-12 mx-auto mb-3 text-blue-400" data-id="1xnir4769" data-path="src/components/HomeAutomationSimulator.tsx" />
            <h4 className="text-lg font-semibold mb-2" data-id="5a8qer349" data-path="src/components/HomeAutomationSimulator.tsx">Seguridad Total</h4>
            <p className="text-gray-400" data-id="2bbcfendr" data-path="src/components/HomeAutomationSimulator.tsx">Monitoreo 24/7 con IA</p>
          </div>
          <div className="text-center" data-id="9emdgrgv3" data-path="src/components/HomeAutomationSimulator.tsx">
            <Mic className="w-12 h-12 mx-auto mb-3 text-green-400" data-id="nveet0top" data-path="src/components/HomeAutomationSimulator.tsx" />
            <h4 className="text-lg font-semibold mb-2" data-id="diohpb5t1" data-path="src/components/HomeAutomationSimulator.tsx">Control por Voz</h4>
            <p className="text-gray-400" data-id="f0dqf2iix" data-path="src/components/HomeAutomationSimulator.tsx">Comandos naturales e intuitivos</p>
          </div>
        </div>
      </div>
    </div>);

};

export default HomeAutomationSimulator;